import clsx from 'clsx';
import { FC, PropsWithChildren, ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeColorPaletteAsCssTokens } from '../../hooks/use-theme-color-palette-as-css-tokens';
import { ThemeSignalColor } from '../../theme';
import { Icon, IconProps } from '../icon';
import { IconButton } from '../icon-button';
import { Stack } from '../stack';
import { Typofallback } from '../typofallback';
import classes from './toast.module.scss';

export type ToastProps = PropsWithChildren & {
  title?: ReactNode;
  icon?: IconProps['icon'];
  color?: ThemeSignalColor;
  className?: string;
  onClose?: () => void;
  ttl?: number;
};

const ICON_MAP = {
  info: ['fas', 'info-circle'],
  warning: ['fas', 'exclamation-circle'],
  danger: ['fas', 'exclamation-circle'],
  success: ['fas', 'check-circle'],
};

export const Toast: FC<ToastProps> = ({
  icon,
  onClose,
  color = 'info',
  className,
  children,
  ttl = 5000,
  title,
  ...props
}) => {
  const [t] = useTranslation();
  const iconToRender = useMemo(() => {
    return (
      (color ? ICON_MAP[color] : undefined)
      ?? (['fas', 'info-circle'])
    ) as IconProps['icon'];
  }, [icon]);

  const styles = useThemeColorPaletteAsCssTokens('--toast', color);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onClose?.();
    }, ttl);

    return () => {
      clearTimeout(timeout);
    };
  }, [ttl]);

  return (
    <div
      {...props}
      className={clsx('toast-component', className, classes.root, {
        [classes.hasCloseButton]: !!onClose,
      })}
      data-variant={color}
      style={{ ...styles }}
    >
      <div className={classes.prefix}>
        <Icon icon={iconToRender} />
      </div>

      <Stack direction="column" spacing={0}>
        {title && <Typofallback variant="label" bold>{title}</Typofallback>}
        <Typofallback>{children}</Typofallback>
      </Stack>

      {onClose && (
        <div className={classes.suffix}>
          <IconButton
            label={t('common.close')}
            className={classes.closeBtn}
            color={color}
            variant="text"
            onClick={onClose}
          >
            <Icon icon={['fas', 'times']} />
          </IconButton>
        </div>
      )}
    </div>
  );
};
